import { createContext, ReactNode, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/shared/components/layout/Modal";
import Button from "@/shared/components/ui/Button";

type ToastVariant = "success" | "error" | "warn";

interface ToastState {
  message: string;
  variant: ToastVariant;
  isVisible: boolean;
  id: string;
}

export interface ToastContextType {
  showToast: (message: string, variant?: ToastVariant) => string;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

const variantIcons = {
  success: (
    <svg
      className="h-6 w-6 text-green-600"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  error: (
    <svg
      className="h-6 w-6 text-red-600"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  ),
  warn: (
    <svg
      className="h-6 w-6 text-yellow-600"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 12.376zM12 15.75h.008v.008H12v-.008z"
      />
    </svg>
  ),
};

export function ToastProvider({ children }: ToastProviderProps) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, variant: ToastVariant = "success") => {
      const id = Math.random().toString(36).substr(2, 9);

      const newToast: ToastState = {
        message,
        variant,
        isVisible: true,
        id,
      };

      setToasts(prev => [...prev, newToast]);

      return id;
    },
    []
  );

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const currentToast = toasts[0];

  return (
    <ToastContext.Provider value={{ showToast, hideToast, clearAllToasts }}>
      {children}

      {currentToast && (
        <Modal
          show={true}
          onHide={() => hideToast(currentToast.id)}
          title={t(
            currentToast.variant === "error" ? "common.error" : "common.success"
          )}
          closable={false}
          footer={
            <div className="flex justify-center w-full">
              <Button size="xl" onClick={() => hideToast(currentToast.id)}>
                {t("common.ok")}
              </Button>
            </div>
          }
        >
          <div className="flex items-start space-x-4 py-2">
            <div className="flex-shrink-0">
              {variantIcons[currentToast.variant]}
            </div>
            <div className="text-gray-700 whitespace-pre-wrap">
              {currentToast.message}
            </div>
          </div>
        </Modal>
      )}
    </ToastContext.Provider>
  );
}

export default ToastContext;
